// BlockGroup > BlockGroup
import React from 'react'
import styled from 'styled-components'
import Scale from '../ScaleData'
import Block from '../Block'
import Text from '../Text'
import { range } from '../utils/Number'
import { BlockGroupProps, BlockGroupState } from './index'

export const defaultProps = Object.freeze({
	disabled: false
})

const BlockGroupWrapper = styled(Block)`
	${({ width }) =>
		width &&
		`width: ${width}%;
	margin-left: ${100 - width}%;`};
	position: relative;
`

const BlockGroupLabel = styled.div`
	position: absolute;
	top: 0px;
	width: 100%;
	text-align: left;
	user-select: none;
	pointer-events: none;
`

const Label = styled(Text)`
	font-size: 11px;
	margin: 4px;
`

export default class BlockGroup<BlockTypesType extends object> extends React.Component<
	BlockGroupProps<BlockTypesType>,
	BlockGroupState
> {
	static readonly defaultProps = defaultProps

	constructor(props: BlockGroupProps<BlockTypesType>) {
		super(props)
		const { scale, groupScale, data } = props
		const initialState = data ? data.initBlockGroupState : {}

		this.state = {
			ixDown: initialState.ixDown ? initialState.ixDown : null,
			lastOver: initialState.lastOver ? initialState.lastOver : null,
			start: groupScale ? groupScale.min : scale.min,
			end: groupScale ? groupScale.max : scale.max
		}

		if (this.state.end <= this.state.start) throw new Error('end must be greater than start')
		if (scale.max < scale.min) throw new Error("scale max can't be less than scale min")
	}

	componentDidUpdate(prevProps: BlockGroupProps<BlockTypesType>) {
		const { ixDown, lastOver } = this.state
		const { scale: prevScale, groupScale: prevGroupScale, gridMouseDownIx: prevGridMouseDownIx } = prevProps
		const { scale, groupScale, gridMouseDownIx } = this.props
		const min = groupScale ? groupScale.min : scale.min
		const max = groupScale ? groupScale.max : scale.max
		const prevMin = prevGroupScale ? prevGroupScale.min : prevScale.min
		const prevMax = prevGroupScale ? prevGroupScale.max : prevScale.max
		const scaleChanged = this.groupScaleChanged(prevGroupScale as Scale)

		const update: Partial<BlockGroupState> = {}

		if (min !== prevMin || scaleChanged) {
			update.start = min
		}

		if (max !== prevMax || scaleChanged) {
			update.end = max
		}

		if (gridMouseDownIx === null && prevGridMouseDownIx !== null && (ixDown || lastOver)) {
			update.ixDown = null
			update.lastOver = null
		}

		if (Object.keys(update).length > 0) {
			this.setState(update as BlockGroupState)
		}
	}

	/**
	 * getters
	 */

	heightPercent = (): number => {
		const { start, end } = this.state
		const { scale } = this.props

		return (end - start) / (scale.max - scale.min) * 100
	}

	topPercent = (): number => {
		const { start } = this.state
		const { scale } = this.props

		return start / (scale.max - scale.min) * 100
	}

	// *****
	// end getters
	// *****

	private groupScaleChanged = (prevGroupScale: Scale) => {
		let result = false
		const { groupScale } = this.props

		if (!groupScale || !prevGroupScale) {
			if (groupScale && !prevGroupScale) result = true
			if (!groupScale && prevGroupScale) result = true

			return result
		}

		if (
			groupScale.min !== prevGroupScale.min ||
			groupScale.max !== prevGroupScale.max ||
			groupScale.increment !== prevGroupScale.increment
		)
			result = true

		return result
	}

	/**
	 * creates the Block instances for the given scale
	 */
	private generateBlocks = (): JSX.Element[] => {
		const { start } = this.state
		const { disabled, scale, groupScale } = this.props
		const blocks: JSX.Element[] = []
		const n = groupScale ? groupScale.totalIncrements : scale.totalIncrements

		for (let i = 0; i < n; i++) {
			const calcedIx = start > 0 ? i + start : start + i

			blocks.push(
				<Block
					key={i}
					index={calcedIx}
					isGroupScale={true}
					scale={groupScale || scale}
					disabled={disabled}
					handleMouseDown={this._handleMouseDown}
					handleMouseUp={this._handleMouseUp}
					handleMouseEnter={this._handleMouseEnter}
				/>
			)
		}

		return blocks
	}

	/**
	 * adds a block to the selected queue
	 */
	_handleMouseDown = (ixDown: number, altKey: boolean) => {
		const { disabled, gridMouseDown, activeMethod, setActiveMethod } = this.props

		// update the super mouse ix state regardless of active state
		if (gridMouseDown) gridMouseDown(ixDown)

		// stop commit if the block is disabled
		if (disabled) return

		this.setState({ ixDown }, () => {
			// if this is a standard click on a selected block then
			// just open up the details for editing
			if (!altKey && activeMethod === 'modify') {
				return
			}

			setActiveMethod(activeMethod)
			this._commitChange([ixDown])
		})
	}

	/**
	 * monitors a mouse down and drag
	 */
	_handleMouseEnter = (index: number) => {
		const { ixDown } = this.state
		const { gridMouseDownIx, gridMouseEnterIx } = this.props

		const stateUpdate: Partial<BlockGroupState> = { lastOver: index }

		// checking the index against the gridMouseEnterIx helps prevent duplicate commit calls when
		// the new div pops up under the mouse
		if ((ixDown || gridMouseDownIx) && index !== gridMouseEnterIx) {
			const mouseDownIx = ixDown ? ixDown : gridMouseDownIx
			const ixRange = mouseDownIx ? range(index, mouseDownIx) : []

			this.setState(stateUpdate as BlockGroupState, () => {
				this._commitChange(ixRange)
			})
		}
	}

	/**
	 * submits the queued indexes to the more steady Grid state
	 */
	_handleMouseUp = (altKey: boolean) => {
		const { ixDown, lastOver } = this.state
		const { gridMouseDown, activeMethod, setActiveMethod, showDetails, data } = this.props

		// reset the ixDown state
		this.setState({ ixDown: null, lastOver: null }, () => {
			if (!altKey && activeMethod === 'modify' && ixDown) {
				if (showDetails && data) showDetails(data)
				return
			}

			// if this was just a single cell mouse 'click' commit the change
			ixDown && lastOver === null && this._commitChange([ixDown])
			setActiveMethod(undefined)

			// -1 since grid is tracking mouse indexes, not just isDown state
			if (gridMouseDown) gridMouseDown(null)
		})
	}

	/**
	 * calcs and sets the necessary adjustments to send along for final commit by Grid
	 */
	_commitChange = (ixRange: number[]) => {
		const { data, callActiveMethod } = this.props

		callActiveMethod(ixRange, data)
	}

	// *****
	// end UX
	// *****

	render() {
		const { start, end } = this.state
		const { data, scale, width, labelConversion, className } = this.props

		return (
			<BlockGroupWrapper
				start={start}
				end={end}
				scale={scale}
				width={width}
				color={data ? data.cssColor : undefined}
				className={className}
			>
				{this.generateBlocks()}
				{data && (
					<BlockGroupLabel>
						<Label>
							{data.label || '(No title)'}
							{end - start > 1 ? <br /> : ', '}
							{labelConversion && `${labelConversion(start)} - ${labelConversion(end)}`}
						</Label>
					</BlockGroupLabel>
				)}
			</BlockGroupWrapper>
		)
	}
}
